import { useRef, useState } from 'react';
import { GaleriIkon, KameraIkon } from '../lib/ikon';
import { useGercekTelefon } from '../lib/cihaz';
import { BELGE_KABUL, fotoYukleCoklu } from '../lib/foto-yukle';

/**
 * FOTOGRAF GIRISI — K8: her fotograf bir is + adim + zamana baglidir.
 *
 * `capture="environment"` telefonda ARKA KAMERAYI dogrudan acar (Android + iOS).
 * Masaustunde normal dosya secici olur.
 *
 * ⚠️ TELEFONDA GALERİ DE AÇILABİLMELİ (kullanıcı isteği 05.08.2026). `capture`
 *    özniteliği yalnız bir tercih değil, bir KISITTIR: konulduğunda tarayıcı
 *    doğrudan kamerayı açar ve galeriye geçiş sunmaz. Sahadaki karşılığı şuydu —
 *    yeşil kartı/izin kâğıdını daha önce çekmiş ya da WhatsApp'tan almış kişi,
 *    elindeki fotoğrafı yükleyemiyor, kâğıdı bulup YENİDEN çekmek zorunda
 *    kalıyordu. Kâğıt ofiste kaldıysa bu imkânsız (K5'in tam da kaçındığı durum).
 *
 * Çözüm iki AYRI `<input>`: biri `capture`'lı, biri değil. Tek input'un
 * özniteliğini tıklama anında değiştirmek denenebilirdi ama iOS'ta öznitelik
 * değişimi aynı karede seçiciye yansımıyor — iki eleman tek davranıştan güvenli.
 *
 * Sıra bilinçli: KAMERA birincil düğme kalır. Sahadaki tipik iş "şimdi çek";
 * galeriyi öne almak, işini doğru yapan çoğunluğa fazladan bir tık yükler.
 *
 * Sikistirma ve gonderim `lib/foto-yukle.ts` icinde — is acma ekrani da ayni
 * yolu kullaniyor ve iki farkli boyut/kalite ayarinin sessizce ayrismasi
 * istenmedi.
 */

export function FotoEkle({
  jobId,
  step,
  etiket,
  gorunum = 'buton',
  onBitti,
}: {
  jobId: string;
  step: string;
  etiket: string;
  /**
   * `buton`  — telefonda tam genişlik, eldivenli parmak için büyük dokunma alanı
   * `kompakt`— masaüstünde foto yuvası / satır içi düğme
   */
  gorunum?: 'buton' | 'kompakt';
  onBitti: () => void | Promise<void>;
}) {
  /*
   * ⚠️ `useGercekTelefon`, `useTelefon` DEĞİL.
   *
   * `useTelefon()` TEMSİL SİMÜLASYONUNDA da true döner — admin masaüstünde
   * "telefon çerçevesi"ni açtığında. Kamera/galeri ayrımı ise bir DÜZEN değil
   * CİHAZ sorusudur ve yanlış cevap sessiz arızaya yol açıyor:
   *
   *   simülasyon açık + masaüstü → `capture="environment"` konuyordu →
   *   tarayıcı web kamerasını açmaya çalışıp dosya seçiciyi HİÇ göstermiyor →
   *   kullanıcı "seçiyorum ama yüklemiyor, uyarı da vermiyor" diyor.
   *
   * Dosyanın altındaki `capture` uyarısı tam olarak bu durumu tarif ediyordu
   * ama koşul yanlış kancaya bağlanmıştı (06.08.2026 saha bildirimi).
   * Masaüstünde "galeri" diye ayrı bir kavram da yok — tek seçici zaten dosyaya
   * gidiyor — bu yüzden ikinci düğme de gerçek cihaza bakar.
   */
  const telefon = useGercekTelefon();
  const kameraGirdi = useRef<HTMLInputElement>(null);
  const galeriGirdi = useRef<HTMLInputElement>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [ilerleme, setIlerleme] = useState<{ biten: number; toplam: number } | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  async function sec(e: React.ChangeEvent<HTMLInputElement>) {
    const alan = e.target;
    const dosyalar = [...(alan.files ?? [])];
    /*
     * ⚠️ SEÇİM İPTAL EDİLDİYSE sessizce çık — kullanıcı dosya seçiciyi kapattı,
     *    bu bir hata değil. Ama dosya VARSA bundan sonrası ASLA sessiz kalmaz.
     */
    if (!dosyalar.length) return;
    setHata(null);
    setYukleniyor(true);
    setIlerleme(dosyalar.length > 1 ? { biten: 0, toplam: dosyalar.length } : null);

    /*
     * ⚠️ try/finally ŞART — `yukleniyor` takılı kalırsa düğme SONSUZA KADAR
     *    disabled olur ve ekranda tek kelime açıklama olmaz. Kullanıcının
     *    gördüğü şey tam olarak şudur: "seçiyorum, yüklemiyor, uyarı da
     *    vermiyor" (06.08.2026 saha bildirimi). `fotoYukle` kendi hatalarını
     *    yakalıyor ama `onBitti()` çağıranın koduna ait ve HER ŞEY atabilir;
     *    o yüzden koruma burada, çağrının etrafında.
     */
    try {
      /*
       * TEK DOSYA ile ÇOK DOSYA aynı yoldan gider; fark yalnız raporlamada.
       * Çok sayfalı belgede biri düşerse ötekiler yüklenmiş olur ve hangisinin
       * eksik kaldığı yazılır — kullanıcı beşini baştan seçmek zorunda kalmaz.
       */
      const sonuc = await fotoYukleCoklu(jobId, step, dosyalar, (biten, toplam) =>
        setIlerleme(toplam > 1 ? { biten, toplam } : null),
      );

      if (sonuc.hatalar.length) {
        const liste = sonuc.hatalar.map((h) => `${h.ad}: ${h.hata}`).join(' · ');
        setHata(
          sonuc.basarili > 0
            ? `${sonuc.basarili}/${dosyalar.length} yüklendi. Yüklenemeyen: ${liste}`
            : liste,
        );
      }
      // Bir tanesi bile girdiyse ekran tazelenmeli — yüklenenler görünsün.
      if (sonuc.basarili > 0) await onBitti();
    } catch (hataObj) {
      /*
       * ⚠️ BEKLENMEYEN HATA DA GÖSTERİLİR. Önceden bu dal hiç yoktu: atılan
       *    bir istisna yakalanmadan geçiyor, `yukleniyor` true kalıyor ve
       *    kullanıcı ölü bir düğmeye bakıyordu. "Sessiz kayıp yok" ilkesi
       *    yüklemeler için de geçerli.
       */
      setHata(hataObj instanceof Error ? hataObj.message : 'Fotoğraf yüklenemedi.');
    } finally {
      setYukleniyor(false);
      setIlerleme(null);
      /*
       * ⚠️ Değeri SIFIRLA. Aksi hâlde aynı dosya ikinci kez seçildiğinde `change`
       *    olayı hiç ateşlenmez ve yükleme sessizce olmaz. Hangi input'tan
       *    geldiğini olaydan alıyoruz — iki ayrı ref'i karıştırmamak için.
       */
      alan.value = '';
    }
  }

  /**
   * ⚠️ `capture` YALNIZ TELEFONDA. Masaüstünde bu öznitelik bazı tarayıcılarda
   *    web kamerasını açmaya çalışıyor ve şef bilgisayarındaki dosyayı
   *    seçemiyor — teslim fotoğrafları oradan yükleniyor.
   *
   * Masaüstünde zaten tek seçici hem dosyaya hem (varsa) kameraya ulaştığı için
   * ikinci düğme çizilmez; orada "galeri" diye ayrı bir kavram yok.
   *
   * ⚠️ ÇOKLU SEÇİM KAMERADA YOK, GALERİDE/DOSYADA VAR. `capture` ile `multiple`
   *    birlikte anlamsız: kamera tek kare çeker. Çok sayfalı belgenin geldiği
   *    yer galeri ya da masaüstündeki dosya seçicisidir.
   */
  const girdiler = (
    <>
      <input
        ref={kameraGirdi}
        type="file"
        accept={telefon ? 'image/*' : BELGE_KABUL}
        {...(telefon ? { capture: 'environment' as const } : { multiple: true })}
        className="hidden"
        onChange={(e) => void sec(e)}
      />
      {telefon && (
        <input
          ref={galeriGirdi}
          type="file"
          accept={BELGE_KABUL}
          multiple
          className="hidden"
          onChange={(e) => void sec(e)}
        />
      )}
    </>
  );

  /** Yüklenirken düğmenin üstündeki metin — çok sayfada "3/5" görünür. */
  const dugmeMetni = yukleniyor
    ? ilerleme
      ? `Yükleniyor ${ilerleme.biten}/${ilerleme.toplam}…`
      : 'Yükleniyor…'
    : etiket;

  if (gorunum === 'kompakt') {
    return (
      <>
        {girdiler}
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <button
            disabled={yukleniyor}
            onClick={() => kameraGirdi.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-(--radius-mod) border border-navy-line bg-navy-bg px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy hover:text-white disabled:opacity-40"
          >
            <KameraIkon boyut={14} />
            {dugmeMetni}
          </button>
          {telefon && (
            <button
              disabled={yukleniyor}
              onClick={() => galeriGirdi.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-(--radius-mod) border border-line-2 px-2.5 py-1.5 text-xs font-medium text-wait hover:border-navy hover:text-navy disabled:opacity-40"
            >
              <GaleriIkon boyut={14} />
              Galeri
            </button>
          )}
        </div>
        {hata && <p className="mt-1 text-xs text-crit">{hata}</p>}
      </>
    );
  }

  return (
    <div>
      {girdiler}
      <button
        disabled={yukleniyor}
        onClick={() => kameraGirdi.current?.click()}
        className="flex w-full items-center justify-center gap-2 rounded-(--radius-mod) bg-navy px-4 py-3.5 text-base font-medium text-white disabled:opacity-40"
      >
        <KameraIkon boyut={18} />
        {dugmeMetni}
      </button>
      {/*
        Galeri İKİNCİL: eldivenli parmak için yine geniş dokunma alanı ama
        görsel ağırlığı düşük — sahadaki tipik iş "şimdi çek".
      */}
      {telefon && (
        <button
          disabled={yukleniyor}
          onClick={() => galeriGirdi.current?.click()}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-(--radius-mod) border border-line-2 px-4 py-2.5 text-sm font-medium text-wait active:bg-ground disabled:opacity-40"
        >
          <GaleriIkon boyut={16} />
          Galeriden seç (çoklu / PDF)
        </button>
      )}
      <p className="mt-1.5 text-center text-xs text-wait">
        Fotoğraf çevrimdışında da çekilebilir, bağlantı gelince gönderilir.
        Çok sayfalı belge için birden fazla dosya seçebilir ya da PDF yükleyebilirsin.
      </p>
      {hata && <p className="mt-2 text-xs text-crit">{hata}</p>}
    </div>
  );
}
